import React, { useRef, useState } from "react";
import { MdCloudUpload, MdDelete, MdInsertDriveFile } from "react-icons/md";

const FileUpload = ({ files = [], onFilesChange, maxFiles = 10, maxSizeMB = 50 }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (newFiles) => {
    if (!onFilesChange || typeof onFilesChange !== 'function') {
      console.error('FileUpload: onFilesChange is not a function', onFilesChange);
      return;
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const validFiles = [];
    const errors = [];

    for (const file of newFiles) {
      if (files.length + validFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        break;
      }

      if (file.size > maxSizeBytes) {
        errors.push(`${file.name} exceeds ${maxSizeMB}MB limit`);
        continue;
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      alert(errors.join("\n"));
    }

    if (validFiles.length > 0) {
      onFilesChange([...files, ...validFiles]);
    }
  };

  const removeFile = (index) => {
    if (!onFilesChange || typeof onFilesChange !== 'function') {
      console.error('FileUpload: onFilesChange is not a function', onFilesChange);
      return;
    }
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (file) => {
    const type = file.type || file.filename?.split('.').pop();
    if (type?.includes('image')) return '🖼️';
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('word') || type?.includes('doc')) return '📝';
    if (type?.includes('excel') || type?.includes('sheet')) return '📊';
    if (type?.includes('zip') || type?.includes('archive')) return '🗜️';
    return '📎';
  };

  return (
    <div className="w-full">
      <label className="text-sm text-navy-700 dark:text-white ml-2 mb-2 block">
        Attachments
      </label>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-200 ${
          dragActive
            ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20"
            : "border-gray-300 dark:border-navy-600 hover:border-brand-400 dark:hover:border-brand-600"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleChange}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
        />

        <MdCloudUpload className="mx-auto text-5xl text-gray-400 dark:text-gray-500 mb-3" />

        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Drag and drop files here, or{" "}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-brand-500 hover:text-brand-600 font-semibold"
          >
            browse
          </button>
        </p>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Supports: Images, PDFs, Documents, Spreadsheets (Max {maxSizeMB}MB, up to {maxFiles} files)
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-white mb-2">
            Uploaded Files ({files.length})
          </p>
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-navy-800 rounded-xl group hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-2xl flex-shrink-0">{getFileIcon(file)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                    {file.name || file.filename}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {file.size ? formatFileSize(file.size) : 'Unknown size'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => removeFile(index)}
                className="flex-shrink-0 p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Remove file"
              >
                <MdDelete className="text-xl" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
